import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Building2, Lock, Key, ArrowLeft, CheckCircle } from 'lucide-react';
import { getMockUsers, saveMockUsers } from '../mock';
import { useToast } from '../hooks/use-toast';

const ResetPassword = () => {
  const [verificationCode, setVerificationCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [step, setStep] = useState(1); // 1: Enter code, 2: Enter new password, 3: Success
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  // Parse reset data once and memoize it
  const resetData = React.useMemo(() => {
    const data = localStorage.getItem('passwordResetData');
    if (!data) return null;
    try {
      return JSON.parse(data);
    } catch {
      return null;
    }
  }, []);

  // Derive resetEmail from resetData instead of using setState
  const derivedResetEmail = resetData?.email || '';

  useEffect(() => {
    // Check if reset data exists
    if (!resetData) {
      navigate('/forgot-password');
      return;
    }
    
    // Check if expired
    if (Date.now() > resetData.expiry) {
      toast({
        title: 'Code Expired',
        description: 'Your verification code has expired. Please request a new one.',
        variant: 'destructive'
      });
      localStorage.removeItem('passwordResetData');
      navigate('/forgot-password');
      return;
    }
  }, [navigate, toast, resetData]);

  const handleVerifyCode = (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const resetData = JSON.parse(localStorage.getItem('passwordResetData'));
    
    if (!resetData) {
      setError('Reset session expired. Please start over.');
      setLoading(false);
      return;
    }

    if (Date.now() > resetData.expiry) {
      setError('Verification code has expired. Please request a new one.');
      setLoading(false);
      localStorage.removeItem('passwordResetData');
      setTimeout(() => navigate('/forgot-password'), 2000);
      return;
    }

    if (verificationCode !== resetData.code) {
      setError('Invalid verification code. Please try again.');
      setLoading(false);
      return;
    }

    // Code is valid
    setLoading(false);
    setStep(2);
    toast({
      title: 'Code Verified!',
      description: 'Please enter your new password.',
    });
  };

  const handleResetPassword = (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Validation
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      setLoading(false);
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    // Update password
    const users = getMockUsers();
    const userIndex = users.findIndex(u => u.email === derivedResetEmail);
    
    if (userIndex === -1) {
      setError('User not found. Please try again.');
      setLoading(false);
      return;
    }

    users[userIndex].password = newPassword;
    saveMockUsers(users);

    // Clear reset data
    localStorage.removeItem('passwordResetData');

    setLoading(false);
    setStep(3);
    
    setTimeout(() => {
      navigate('/login');
    }, 3000);
  };

  const getTimeRemaining = useCallback(() => {
    const resetData = localStorage.getItem('passwordResetData');
    if (!resetData) return '0:00';
    
    const data = JSON.parse(resetData);
    const remaining = Math.max(0, data.expiry - Date.now());
    const minutes = Math.floor(remaining / 60000);
    const seconds = Math.floor((remaining % 60000) / 1000);
    
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }, []);

  const [timeRemaining, setTimeRemaining] = useState('0:00');

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeRemaining(getTimeRemaining());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-teal-50 via-cyan-50 to-orange-50 p-4 relative">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-2xl mb-4 shadow-lg">
            <Building2 className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-4xl font-bold bg-gradient-to-r from-teal-600 to-cyan-600 bg-clip-text text-transparent">
            MyVenueMate
          </h1>
          <p className="text-gray-600 dark:text-muted-foreground mt-2">Reset Your Password</p>
        </div>

        <Card className="shadow-xl border-0">
          <CardHeader>
            <CardTitle>
              {step === 1 && 'Enter Verification Code'}
              {step === 2 && 'Create New Password'}
              {step === 3 && 'Password Reset Successful'}
            </CardTitle>
            <CardDescription>
              {step === 1 && `Code sent to ${derivedResetEmail}`}
              {step === 2 && 'Choose a strong password for your account'}
              {step === 3 && 'Redirecting to login...'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {step === 1 && (
              <form onSubmit={handleVerifyCode} className="space-y-4">
                {error && (
                  <Alert variant="destructive">
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                <div className="bg-teal-50 dark:bg-teal-900/20 border border-teal-200 rounded-lg p-3 mb-4">
                  <p className="text-sm text-teal-800">
                    <strong>Time Remaining:</strong> {timeRemaining}
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="code">Verification Code</Label>
                  <div className="relative">
                    <Key className="absolute left-3 top-3 h-4 w-4 text-gray-400 dark:text-muted-foreground" />
                    <Input
                      id="code"
                      type="text"
                      placeholder="Enter 6-digit code"
                      value={verificationCode}
                      onChange={(e) => setVerificationCode(e.target.value)}
                      className="pl-10 text-center text-2xl tracking-widest"
                      maxLength={6}
                      required
                    />
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700 transition-all duration-300 shadow-md"
                  disabled={loading}
                >
                  {loading ? 'Verifying...' : 'Verify Code'}
                </Button>

                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => navigate('/forgot-password')}
                    className="text-sm text-teal-600 hover:text-teal-700"
                  >
                    Didn&apos;t receive code? Resend
                  </button>
                </div>
              </form>
            )}

            {step === 2 && (
              <form onSubmit={handleResetPassword} className="space-y-4">
                {error && (
                  <Alert variant="destructive">
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                <div className="space-y-2">
                  <Label htmlFor="newPassword">New Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400 dark:text-muted-foreground" />
                    <Input
                      id="newPassword"
                      type="password"
                      placeholder="Min. 8 characters"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="pl-10"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirm New Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400 dark:text-muted-foreground" />
                    <Input
                      id="confirmPassword"
                      type="password"
                      placeholder="Confirm password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="pl-10"
                      required
                    />
                  </div>
                </div>

                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 rounded-lg p-3">
                  <p className="text-xs text-blue-800">
                    <strong>Password Requirements:</strong>
                  </p>
                  <ul className="text-xs text-blue-700 mt-1 space-y-1">
                    <li>• At least 8 characters long</li>
                    <li>• Use a mix of letters and numbers</li>
                    <li>• Avoid common passwords</li>
                  </ul>
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700 transition-all duration-300 shadow-md"
                  disabled={loading}
                >
                  {loading ? 'Resetting...' : 'Reset Password'}
                </Button>
              </form>
            )}

            {step === 3 && (
              <div className="text-center py-6">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full mb-4">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-foreground mb-2">
                  Password Reset Successful!
                </h3>
                <p className="text-sm text-gray-600 dark:text-muted-foreground mb-4">
                  Your password has been updated successfully.
                </p>
                <p className="text-xs text-gray-500 dark:text-muted-foreground">
                  Redirecting to login page...
                </p>
              </div>
            )}

            {step !== 3 && (
              <div className="mt-6 text-center">
                <button
                  type="button"
                  onClick={() => navigate('/login')}
                  className="text-sm text-gray-600 dark:text-muted-foreground hover:text-teal-600 transition-colors inline-flex items-center"
                >
                  <ArrowLeft className="w-4 h-4 mr-1" />
                  Back to Login
                </button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Powered by footer */}
      <div className="absolute bottom-4 left-0 right-0 text-center">
        <p className="text-xs text-gray-500 dark:text-muted-foreground">
          Powered by <span className="font-semibold text-teal-600">MYONK</span>
        </p>
      </div>
    </div>
  );
};

export default ResetPassword;
